import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Toaster } from "react-hot-toast"

import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css"
import "./chat-override.css"

import { useChatStore } from "../../state/chat-store"
import { useHeartbeat } from "../../state/use-heartbeat"
import { useAppVisibility } from "../../state/use-app-visibility"

import { ChannelsSidebar } from "./channels-sidebar"
import { ChannelDetailsSidebar } from "./channel-details-sidebar"
import { MessagesArea } from "./messages-area"
import * as env from '@/env'
import { useSessionContext } from '@/state/session-context'
import { fetchUser } from '@/state/user-actions'

export default function Chat() {
	const urlParams = useParams()
	const navigate = useNavigate()
	
	const [isChannelsOpen, setIsChannelsOpen] = useState(false)
	const [isDetailsOpen, setIsDetailsOpen] = useState(false)

	const setActiveChannelId = useChatStore(state => state.setActiveChannelId)

	const { session, isLoading, logout } = useSessionContext()

	useHeartbeat({ enabled: !!session?.userId })

	useAppVisibility({
		onForeground: () => {
			//	fire-and-forget: fetchUser may throw on transient failure (see user-actions.ts).
			//	attach a .catch() so we don't produce an unhandled promise rejection.
			//	nothing to do on failure — this is a best-effort background refresh.
			if (session?.userId)
				fetchUser(session.userId)
					.catch(err => console.warn('foreground fetchUser failed (transient):', err)) /*noawait*/
		},
		enabled: !!session?.userId
	})

	useEffect(() => {
		if (!isLoading && (!session || !session.userId)) {
			navigate('/')
			return
		}
		if (session?.userId) {
			const fetchUser_ = async () => {
				try {
					const user = await fetchUser(session.userId)
					//	null == definitive auth loss (401/403). only then bounce out.
					if (!user) {
						await logout()
						navigate('/')
					}
				} catch (err) {
					//	transient failure (network / socket / 5xx / task-worker timeout).
					//	leave the session and route intact — useAppVisibility's foreground
					//	refetch or a manual reload will self-heal on the next opportunity.
					//	only 401/403 (returned as null above) is a definitive log-out signal.
					console.warn('Transient user fetch on chat mount; leaving session intact:', err)
				}
			}
			fetchUser_()
		}
	}, [session, isLoading, navigate, logout])

	useEffect(() => {
		if (urlParams.channelId) {
			setActiveChannelId(urlParams.channelId)
			navigate('/chat', { replace: true })
		}
	}, [urlParams.channelId, setActiveChannelId, navigate])

	//	disable in production; will enable on launch
	// if (env.isProduction) {
	// 	return <div>Nothing to see here, please disperse.</div>
	// }

	//	gate the chat shell on auth resolution. avoids a flash of a half-rendered shell
	//	(empty sidebar, no channels) on logged-out navigation to /chat before the
	//	useEffect above redirects to '/'.
	if (isLoading || !session?.userId) return null

	return (
		<div className="flex-1 h-full w-full flex flex-col">
			{/* main */}
			<div className="flex-1 flex overflow-hidden">
				<ChannelsSidebar
					isOpen={isChannelsOpen}
					onClose={() => setIsChannelsOpen(false)}
				/>

				<div className={`flex-1 flex flex-col min-w-0 transition-all duration-200 ease-in-out ${isDetailsOpen ? 'md:mr-64' : ''}`}>
					<MessagesArea
						onToggleChannels={() => setIsChannelsOpen(!isChannelsOpen)}
						onToggleDetails={() => setIsDetailsOpen(!isDetailsOpen)}
					/>
				</div>

				<ChannelDetailsSidebar
					isOpen={isDetailsOpen}
					onClose={() => setIsDetailsOpen(false)}
				/>
			</div>

			{/* Mobile overlay */}
			{(isChannelsOpen || isDetailsOpen) && (
				<div 
					className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
					onClick={() => {
						setIsChannelsOpen(false)
						setIsDetailsOpen(false)
					}}
				/>
			)}

			<Toaster />
		</div>
	)	
}
